import { useMemo, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Button } from "@patient-watch/ui/button";
import { Download, Plus } from "lucide-react";
import { toast } from "@patient-watch/ui/sonner";
import { CompaniesTable } from "@/features/lead-gen/components/CompaniesTable";
import { LeadGenFilterSelect } from "@/features/lead-gen/components/LeadGenFilterSelect";
import { LeadGenFiltersDialog } from "@/features/lead-gen/components/LeadGenFiltersDialog";
import { LeadGenNavTabs, LeadGenSearch } from "@/features/lead-gen/components/LeadGenNavTabs";
import { companyFilterCount, compactFilters, type CompanyListFilters } from "@/features/lead-gen/leadGenFilters";
import { useLeadGen } from "@/features/lead-gen/LeadGenContext";
import { useNav } from "@/utils/nav";

export const Route = createFileRoute("/_app/sales/leads/companies/")({
    component: LeadCompaniesPage,
});

function LeadCompaniesPage() {
    const { companies, renameCompany, setCompanyTeams, setCompanyUrl, removeCompany, exportSql } = useLeadGen();
    const [searchQuery, setSearchQuery] = useState("");
    const [filters, setFilters] = useState<CompanyListFilters>({});

    useNav([
        { label: "Sales", route: { to: "/sales/leads" } },
        { label: "Leads", route: { to: "/sales/leads" } },
        { label: "Companies", route: { to: "/sales/leads/companies" } },
    ]);

    const teamFilterOptions = useMemo(
        () =>
            Array.from(new Set(companies.flatMap((company) => company.teams).filter(Boolean)))
                .toSorted((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
                .map((value) => ({ value, label: value })),
        [companies]
    );

    return (
        <>
            <LeadGenNavTabs
                toolbar={
                    <>
                        <LeadGenFiltersDialog
                            activeCount={companyFilterCount(filters)}
                            onClear={() => setFilters({})}
                            title="Filter companies"
                        >
                            <LeadGenFilterSelect
                                id="companies-filter-team"
                                label="Specialty"
                                value={filters.team ?? ""}
                                onChange={(team) => setFilters(compactFilters({ ...filters, team }))}
                                options={teamFilterOptions}
                                allLabel="All specialties"
                            />
                        </LeadGenFiltersDialog>
                        <LeadGenSearch
                            value={searchQuery}
                            onChange={setSearchQuery}
                            placeholder="Search companies..."
                        />
                    </>
                }
                action={
                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="min-h-0 shrink-0"
                            onClick={() => {
                                exportSql();
                                toast.success("SQL export downloaded");
                            }}
                        >
                            <Download className="w-3.5 h-3.5" />
                            Export SQL
                        </Button>
                        <Button type="button" size="sm" className="min-h-0 shrink-0" asChild>
                            <Link to="/sales/leads/companies/new">
                                <Plus className="w-3.5 h-3.5" />
                                Add company
                            </Link>
                        </Button>
                    </div>
                }
            />

            <CompaniesTable
                companies={companies}
                onRename={renameCompany}
                onTeamsChange={setCompanyTeams}
                onUrlChange={setCompanyUrl}
                onRemove={removeCompany}
                filters={filters}
                onFiltersChange={setFilters}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
            />
        </>
    );
}
